import React from 'react';
import { Loader2, X } from 'lucide-react';

/**
 * BulkSendProgress
 * ────────────────
 * Unified progress visualisation for any bulk-outreach flow across the
 * platform. Originally lifted from the Pharma Contact "Email All" dialog
 * (which the user singled out as the cleanest pattern) and turned into a
 * reusable component so every bulk-send dialog shows the same UX:
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ ⟳  Sending emails…                       6 / 18  │
 *   │ ████████████░░░░░░░░░░░░░░░░░░░░░░░░░░░          │
 *   │ 6 sent   12 remaining                       33%  │
 *   └──────────────────────────────────────────────────┘
 *
 * Drop it inside any Dialog above the form fields. It will render in
 * three colour-coded states:
 *   • running   → sky blue, spinner icon
 *   • completed → emerald, check icon, "All N emails sent!"
 *   • failed    → rose, X icon, "Sending failed"
 *
 * Props
 *   progress   { status, sent, total, failed } | null
 *              When `null` the component renders nothing — caller can
 *              keep it permanently mounted and toggle visibility purely
 *              via state.
 *   label      Optional verb override for the running state.
 *              Defaults to "Sending emails…" but pages dealing with
 *              briefs, alerts, or notifications can pass e.g.
 *              "Sending updates…" / "Pitching investors…".
 *   testId     Optional data-testid for the wrapper (default
 *              "bulk-send-progress").
 */
export default function BulkSendProgress({ progress, label = 'Sending emails…', testId = 'bulk-send-progress', onDone }) {
  if (!progress) return null;

  const status = progress.status || 'running';
  const sent = Number(progress.sent || 0);
  const total = Number(progress.total || 0);
  const failed = Number(progress.failed || 0);
  const remaining = Math.max(0, total - sent - failed);
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;

  const isDone = status === 'completed';
  const isFailed = status === 'failed';
  const wrapperCls = isDone
    ? 'bg-emerald-50 border-emerald-200'
    : isFailed
    ? 'bg-rose-50 border-rose-200'
    : 'bg-sky-50 border-sky-200';
  const barCls = isDone ? 'bg-emerald-500' : isFailed ? 'bg-rose-500' : 'bg-sky-500';

  return (
    <div className={`rounded-lg p-4 border -mx-1 ${wrapperCls}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isDone ? (
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : isFailed ? (
            <div className="w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center">
              <X className="w-3 h-3 text-white" />
            </div>
          ) : (
            <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
          )}
          <span className="text-sm font-semibold text-slate-800">
            {isDone ? `All ${sent} email${sent === 1 ? '' : 's'} sent!` : isFailed ? 'Sending failed' : label}
          </span>
        </div>
        <span className="text-sm font-bold text-slate-700">{sent}/{total}</span>
      </div>
      <div className="w-full bg-white/60 rounded-full h-2.5 border border-slate-200">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${barCls}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex gap-3">
          <span className="text-[11px] text-emerald-600 font-medium">{sent} sent</span>
          {failed > 0 && (
            <span className="text-[11px] text-rose-600 font-medium">{failed} failed</span>
          )}
          {remaining > 0 && (
            <span className="text-[11px] text-slate-500">{remaining} remaining</span>
          )}
        </div>
        <span className="text-[10px] text-slate-400">{pct}%</span>
      </div>
      {/* Feb 2026 · "Done" button appears once sending finishes (either
          `completed` or `failed`). Closes the parent dialog / resets its
          progress state via the injected onDone callback. The campaign
          history / audit trail is preserved on the server regardless —
          this button is purely a UI-close affordance so the user has a
          clear "I'm done" action without hunting for the X icon. */}
      {(isDone || isFailed) && onDone && (
        <div className="mt-3 pt-3 border-t border-white/50 flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">
            {isDone
              ? 'Summary saved to campaign history.'
              : 'Partial run — you can retry failed rows or close.'}
          </span>
          <button
            type="button"
            onClick={onDone}
            className={
              `text-xs font-semibold px-4 py-1.5 rounded-md transition-colors ` +
              (isDone
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-slate-700 hover:bg-slate-800 text-white')
            }
            data-testid={`${testId}-done-btn`}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
